/**
 * DeckDesignEditor — editing a deck's words ON the deck.
 *
 * The Edit tab used to draw its own plain slide sheets, so a designed deck
 * lost its design the moment you went to change a word in it: you edited one
 * object and previewed another. This is the SAME rendering the View tab shows —
 * the real design, from the same endpoint — with its text boxes made editable
 * where each one maps onto exactly one value in the model the deck was built
 * from. Nothing is re-styled for editing; you type on the slide.
 *
 * The renderer stamps `data-dm` on precisely those boxes and on no others, so
 * what is editable here is decided at the point the design is drawn rather than
 * guessed at from the DOM. Brand chrome, a placeholder standing in for an
 * absent field, and text spliced together from two values carry no address and
 * so are shown exactly as they render but cannot be typed into — editing them
 * would invent content or write to the wrong place.
 *
 * Saving does not patch the file. It sends back the words, and the server
 * RE-RENDERS the deck from its edited model through the design skill that made
 * it — the same path the agent takes — so no amount of editing can degrade the
 * design.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Spin } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import './SlideDeckView.css';

export interface DeckDesignHandle {
  /** Send the edits. Resolves true when something was saved. */
  save: () => Promise<boolean>;
}

interface Props {
  fileId: string;
  title?: string;
  onDirtyChange?: (dirty: boolean) => void;
  /** No design for this file — the caller should fall back to its own editor. */
  onNoDesign?: () => void;
}

/** Affordance, injected into the design document rather than baked into the
 *  renderer: the design must render identically whether or not it is being
 *  edited, so these rules exist only while the Edit tab is open. */
const EDIT_CSS = `
[data-dm] { outline: 1px dashed rgba(0,0,0,0); border-radius: 3px; transition: outline-color .12s, background .12s; }
[data-dm]:hover { outline-color: rgba(99,102,241,.55); background: rgba(99,102,241,.06); cursor: text; }
[data-dm]:focus { outline: 2px solid rgba(99,102,241,.9); background: rgba(99,102,241,.08); }
`;

/** The words in a box, as the DECK stores them — not as the design draws them.
 *
 *  `innerText` is layout-aware, so it hands back what `text-transform` painted:
 *  a section heading the design sets in capitals reads as "WHAT SHIPPED THIS
 *  QUARTER" however it is written in the file. Saving that would quietly
 *  rewrite the stored title in capitals, and it would stay that way — the
 *  design's styling baked into the content it is supposed to be styling.
 *  `textContent` ignores the transform and returns what is actually there.
 */
const textOf = (el: HTMLElement): string => el.textContent ?? '';

export const DeckDesignEditor = forwardRef<DeckDesignHandle, Props>(
  ({ fileId, title, onDirtyChange, onNoDesign }, ref) => {
    const { t } = useLanguage();
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [html, setHtml] = useState<string | null | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    /** What each box said when the design was last drawn — the baseline a save
     *  is diffed against, so an untouched box is never sent. */
    const baseline = useRef<Record<string, string>>({});
    /** The file version this drawing was made from — sent back with the edits
     *  so a deck that changed underneath us is refused, not overwritten. */
    const version = useRef<number | null>(null);

    const load = useCallback(() => {
      setHtml(undefined);
      return api.get(`/vault/files/${fileId}/deck-design`)
        .then((res) => {
          const got = (res.data?.html as string) || null;
          version.current = (res.data?.version as number) ?? null;
          setHtml(got);
          if (!got) onNoDesign?.();
        })
        .catch(() => { setHtml(null); onNoDesign?.(); });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId]);

    useEffect(() => { load(); }, [load]);

    /** Make the addressable boxes editable once the design has painted. */
    const onFrameLoad = useCallback(() => {
      const doc = frameRef.current?.contentDocument;
      if (!doc) return;
      const style = doc.createElement('style');
      style.textContent = EDIT_CSS;
      doc.head?.appendChild(style);

      const next: Record<string, string> = {};
      doc.querySelectorAll<HTMLElement>('[data-dm]').forEach((el) => {
        const key = el.getAttribute('data-dm')!;
        next[key] = textOf(el);
        el.contentEditable = 'true';
        el.spellcheck = false;
      });
      baseline.current = next;

      // One listener on the document, not one per box: boxes are recreated on
      // every reload, and per-box listeners would accumulate with them.
      doc.addEventListener('input', () => onDirtyChange?.(true));
      // Enter would otherwise inject a <div>/<br> into a box whose text goes
      // straight into a .pptx run. A slide box is one paragraph here.
      doc.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') e.preventDefault();
      });
      // Paste as plain text — the design decides how text looks, not whatever
      // markup happened to be on the clipboard.
      doc.addEventListener('paste', (e) => {
        const ev = e as ClipboardEvent;
        const text = ev.clipboardData?.getData('text/plain');
        if (text == null) return;
        ev.preventDefault();
        doc.getSelection()?.getRangeAt(0)?.deleteContents();
        doc.getSelection()?.getRangeAt(0)?.insertNode(doc.createTextNode(text));
        doc.getSelection()?.collapseToEnd();
        onDirtyChange?.(true);
      });
    }, [onDirtyChange]);

    useImperativeHandle(ref, () => ({
      save: async () => {
        const doc = frameRef.current?.contentDocument;
        if (!doc) return false;
        const edits: Record<string, string> = {};
        doc.querySelectorAll<HTMLElement>('[data-dm]').forEach((el) => {
          const key = el.getAttribute('data-dm')!;
          const now = textOf(el);
          if (now !== baseline.current[key]) edits[key] = now;
        });
        if (!Object.keys(edits).length) {
          onDirtyChange?.(false);
          return false;
        }
        setSaving(true);
        try {
          await api.put(`/vault/files/${fileId}/deck-design`,
                        { edits, version: version.current });
          onDirtyChange?.(false);
          // Draw it again from the server. The deck re-flows on save — text
          // that grew may shrink to fit or push a slide onto a continuation
          // page — so showing the pre-save DOM would show a deck the file no
          // longer is, and would leave the baseline pointing at stale
          // addresses.
          await load();
          return true;
        } finally {
          setSaving(false);
        }
      },
    }), [fileId, load, onDirtyChange]);

    if (html === undefined || saving) {
      return <div className="deck-preview-loading"><Spin /></div>;
    }
    if (html === null) return null;

    return (
      <div className="deck-design-edit">
        <iframe
          ref={frameRef}
          className="artifact-html-frame"
          title={title || t('vault.viewer.deckFrameTitle')}
          sandbox="allow-same-origin"
          srcDoc={html}
          onLoad={onFrameLoad}
        />
      </div>
    );
  },
);

DeckDesignEditor.displayName = 'DeckDesignEditor';

export default DeckDesignEditor;
